import { useCallback, useRef } from 'react';
import axios from 'axios';

export const isAbortError = (error: unknown): boolean =>
  axios.isCancel(error) || (error as { code?: string })?.code === 'ERR_CANCELED';

/**
 * Shared AbortController helper: abort previous in-flight request and start a new one.
 */
export const useAbortableRequest = () => {
  const abortRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  /** Abort any previous controller and return a fresh one for the next request. */
  const next = useCallback(() => {
    abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller;
  }, [abort]);

  const isCurrent = useCallback(
    (controller: AbortController) => abortRef.current === controller,
    [],
  );

  return { abort, next, isCurrent, abortRef };
};
